import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { MaintenanceOverviewService } from '../../services/maintenance-overview.service';
import { AccountFilterComponent } from '../../../../../_shared/components/filters/account-filter/account-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../../_shared/models';
import {
  CalendarConfig,
  CalendarEvent,
  CalendarTodayEvent,
  CalendarFilterOption,
  CalendarDay
} from '../../../../_shared/models/calendar.model';
import { DateInterval } from '../../../../../_shared/enums';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { EventScheduleModalComponent } from '../modals/event-schedule-modal/event-schedule-modal.component';
import { SharedCalendarComponent } from '../../../../_shared/components/calendar/calendar.component';
import {
  CalendarEventsByDateRequest,
  TodayCalendarEventsPaginationRequest,
  CalendarEventsPreviewRequest,
  DragAndDropEventRequest
} from '../../models/maintenance-calendar-request.model';
import { CalendarEventResponse, EventScheduleResponse, TodayCalendarEventsResponse } from '../../models/maintenance-calendar-response.model';
import { MaintenanceEventType } from '../../enums/maintenance-overview.enum';
import { endOfDay, endOfMonth, endOfWeek, isBefore, isToday, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-maintenance-calendar-overview',
  standalone: true,
  imports: [CommonModule, SharedCalendarComponent, AccountFilterComponent],
  templateUrl: './maintenance-calendar-overview.component.html',
  styleUrl: './maintenance-calendar-overview.component.scss'
})
export class MaintenanceCalendarOverviewComponent implements OnInit, OnDestroy {
  private filterService = inject(FilterService);
  private maintenanceService = inject(MaintenanceOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);

  // Filter state
  accountFilter: AccountFilterData = {} as AccountFilterData;
  dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;

  // Calendar configuration
  calendarConfig: CalendarConfig = {
    title: this.transloco.translate('maintenanceOverview.calendar.title'),
    backLink: '/asset-management/maintenance-overview',
    searchPlaceholder: this.transloco.translate('maintenanceOverview.calendar.searchPlaceholder'),
    filterSectionTitle: this.transloco.translate('maintenanceOverview.calendar.filterSectionTitle'),
    filterOptions: [
      { key: 'preventative', label: this.transloco.translate('maintenanceOverview.calendar.preventative'), color: '#3873C9', checked: true },
      { key: 'reactive', label: this.transloco.translate('maintenanceOverview.calendar.reactive'), color: '#F5A623', checked: true }
    ],
    missedEventFilterSectionTitle: this.transloco.translate('maintenanceOverview.calendar.missedEventFilterSectionTitle'),
    missedEventFilter: { key: 'missed', label: this.transloco.translate('maintenanceOverview.calendar.missedService'), color: '#9FA1A4', checked: true },
    todaySectionTitle: this.transloco.translate('maintenanceOverview.calendar.today'),
    showTodaySection: true
  };

  // Date filter config
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];

  // Data
  isLoadingAllEvents: boolean = true;
  isLoadingTodayEvents: boolean = true;
  expandedDayLoadingKey: string | null = null;
  previewEvents: CalendarEvent[] = [];
  expandedDayEvents: CalendarEvent[] = [];
  expandedDayKey: string | null = null;
  todayEvents: CalendarTodayEvent[] = [];
  todayPage: number = 1;
  todayTotalPages: number = 0;
  isEventScheduleModalOpen: boolean = false;
  isUpdatingDraggedEvent: boolean = false;

  // Internal state
  private searchTerm: string = '';
  private currentDate: Date = new Date();
  private cancelSubject: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.transloco.selectTranslation().pipe(takeUntil(this.cancelSubject)).subscribe(() => {
      this.refreshCalendarLabels();
    });
  }

  private refreshCalendarLabels(): void {
    const translate = (key: string) => this.transloco.translate(key);
    const labels: Record<string, string> = {
      preventative: translate('maintenanceOverview.calendar.preventative'),
      reactive: translate('maintenanceOverview.calendar.reactive'),
    };
    this.calendarConfig = {
      ...this.calendarConfig,
      title: translate('maintenanceOverview.calendar.title'),
      searchPlaceholder: translate('maintenanceOverview.calendar.searchPlaceholder'),
      filterSectionTitle: translate('maintenanceOverview.calendar.filterSectionTitle'),
      filterOptions: this.calendarConfig.filterOptions.map(option => ({ ...option, label: labels[option.key] ?? option.label })),
      missedEventFilterSectionTitle: translate('maintenanceOverview.calendar.missedEventFilterSectionTitle'),
      missedEventFilter: this.calendarConfig.missedEventFilter ? {
        ...this.calendarConfig.missedEventFilter,
        label: translate('maintenanceOverview.calendar.missedService'),
      } : undefined,
      todaySectionTitle: translate('maintenanceOverview.calendar.today'),
    };
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  // Account filter handler
  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    this.updateFilter();
  }

  // Date filter handler from calendar
  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.updateFilter();
  }

  private updateFilter(): void {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filterService.updateFilter({
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData);

      this.resetMonthCalendarEventsState();
      this.resetTodayCalendarEventsState();
    }
  }

  onFilterChange(options: CalendarFilterOption[]): void {
    this.calendarConfig.filterOptions = options;
    this.clearExpandedDayEvents();
    void this.loadCalendarEventsForCurrentView();
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.clearExpandedDayEvents();
    void this.loadCalendarEventsForCurrentView();
  }

  onDateChange(dates: { currentDate: Date; selectedDate: Date }): void {
    this.currentDate = dates.currentDate;
    const pivot = dates.selectedDate ?? this.currentDate;

    switch (this.dateFilter.interval) {
      case DateInterval.Day:
        this.dateFilter.startDate = startOfDay(pivot);
        this.dateFilter.endDate = endOfDay(pivot);
        break;
      case DateInterval.Week:
        this.dateFilter.startDate = startOfWeek(pivot, { weekStartsOn: 0 });
        this.dateFilter.endDate = endOfWeek(pivot, { weekStartsOn: 0 });
        break;
      case DateInterval.Month:
      default:
        this.dateFilter.startDate = startOfMonth(this.currentDate);
        this.dateFilter.endDate = endOfMonth(this.currentDate);
        break;
    }

    void this.resetMonthCalendarEventsState();
  }

  onTodayScrollDown(): void {
    if (this.isLoadingTodayEvents || this.todayPage >= this.todayTotalPages) return;

    this.todayPage++;
    void this.loadTodayCalendarEventsData();
  }

  onMonthDayExpand(day: CalendarDay): void {
    const dayKey = this.getDayKey(day.date);

    if (this.expandedDayLoadingKey === dayKey) {
      return;
    }

    void this.loadCalendarEventsByDateData(day.date);
  }

  async onEventClick(event: CalendarEvent): Promise<void> {
    if (this.isEventScheduleModalOpen) {
      return;
    }

    this.isEventScheduleModalOpen = true;

    try {
      const response = await this.maintenanceService.getEventSchedule(event.id, this.cancelSubject);
      if (!response.data) {
        this.isEventScheduleModalOpen = false;
        return;
      }

      const modalRef = this.modalService.open(EventScheduleModalComponent, {
        size: 'md',
        centered: true,
        backdrop: 'static'
      });

      modalRef.componentInstance.workOrderId = event.id;
      modalRef.componentInstance.eventData = this.normalizeEventScheduleResponse(response.data);

      modalRef.closed.subscribe(result => {
        this.isEventScheduleModalOpen = false;
        if (result === true) {
          void this.refreshCalendarDataAfterEventScheduleUpdate();
        }
      });

      modalRef.dismissed.subscribe(() => {
        this.isEventScheduleModalOpen = false;
      });
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isEventScheduleModalOpen = false;
        }
      });
    }
  }

  async onEventDrop(data: { event: CalendarEvent; targetDay: CalendarDay }): Promise<void> {
    if (this.isUpdatingDraggedEvent) {
      return;
    }

    if (isBefore(startOfDay(data.targetDay.date), startOfDay(new Date()))) {
      this.toastr.warning(this.transloco.translate('maintenanceOverview.toasts.eventsCannotBeScheduledInPast'));
      return;
    }

    this.isUpdatingDraggedEvent = true;
    const previousPreviewEvents = this.cloneCalendarEvents(this.previewEvents);
    const originalScheduledDate = new Date(data.event.scheduledDate);
    const scheduledDate = this.mergeTargetDateWithEventTime(data.targetDay.date, originalScheduledDate);
    const refreshToday = isToday(originalScheduledDate) || isToday(scheduledDate);

    if (refreshToday) {
      this.isLoadingTodayEvents = true;
    }

    try {
      if (data.targetDay.isCurrentMonth) {
        this.applyOptimisticEventDrop(data.event.id, scheduledDate);
      } else {
        this.previewEvents = this.previewEvents.filter(event => event.id !== data.event.id);
      }

      const request: DragAndDropEventRequest = {
        workOrderId: data.event.id,
        assetId: data.event.assetId,
        scheduledDate
      };

      await this.maintenanceService.rescheduleEvent(request, this.cancelSubject);
      await this.refreshCalendarDataAfterEventScheduleUpdate(false, refreshToday, refreshToday);

      this.isUpdatingDraggedEvent = false;
    } catch (error) {
      if (refreshToday) {
        this.isLoadingTodayEvents = false;
      }

      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.previewEvents = previousPreviewEvents;
          this.isUpdatingDraggedEvent = false;
        }
      });
    }
  }

  private async resetTodayCalendarEventsState(showLoader: boolean = true): Promise<void> {
    this.todayPage = 1;
    this.todayTotalPages = 0;
    this.todayEvents = [];

    await this.loadTodayCalendarEventsData(showLoader);
  }

  private async resetMonthCalendarEventsState(showLoader: boolean = true): Promise<void> {
    this.expandedDayLoadingKey = null;
    this.clearExpandedDayEvents();
    this.previewEvents = [];

    await this.loadCalendarEventsForCurrentView(showLoader);
  }

  private async refreshCalendarDataAfterEventScheduleUpdate(
    showLoader: boolean = true,
    refreshToday: boolean = true,
    showTodayLoader: boolean = showLoader
  ): Promise<void> {
    this.expandedDayLoadingKey = null;
    this.clearExpandedDayEvents();


    await Promise.all([
      this.loadCalendarEventsForCurrentView(showLoader),
      refreshToday ? this.resetTodayCalendarEventsState(showTodayLoader) : Promise.resolve()
    ]);
  }

  private async loadCalendarEventsForCurrentView(showLoader: boolean = true): Promise<void> {
    if (this.dateFilter.interval === DateInterval.Month || this.dateFilter.interval == null) {
      await this.loadCalendarEventsPreviewData(showLoader);
    } else {
      await this.loadAllCalendarEventsForRange(showLoader);
    }
  }

  private async loadTodayCalendarEventsData(showLoader: boolean = true): Promise<void> {
    if (showLoader) {
      this.isLoadingTodayEvents = true;
    }

    try {
      const response = await this.maintenanceService.getTodayCalendarEvents(
        this.createTodayCalendarRequest(),
        this.cancelSubject
      );
      const todayCalendarEvents = response.data?.results ?? [];
      const newTodayEvents = todayCalendarEvents.map(event => this.mapTodayEvent(event));

      this.todayEvents = this.todayPage === 1
        ? newTodayEvents
        : [...this.todayEvents, ...newTodayEvents];
      this.todayTotalPages = response.data?.totalPages ?? 0;

      if (showLoader) {
        this.isLoadingTodayEvents = false;
      }
    } catch(error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          if (showLoader) {
            this.isLoadingTodayEvents = false;
          }
        }
      });
    }
  }

  private async loadCalendarEventsPreviewData(showLoader: boolean = true): Promise<void> {
    if (showLoader) {
      this.isLoadingAllEvents = true;
    }

    try {
      const request = this.createCalendarEventsPreviewRequest();
      const response = await this.maintenanceService.getCalendarEventsPreview(request, this.cancelSubject);

      this.previewEvents = (response.data ?? []).map(event => this.mapCalendarEvent(event));
      if (showLoader) {
        this.isLoadingAllEvents = false;
      }
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          if (showLoader) {
            this.isLoadingAllEvents = false;
          }
        }
      });
    }
  }

  private async loadCalendarEventsByDateData(date: Date): Promise<void> {
    const dayKey = this.getDayKey(date);
    this.expandedDayLoadingKey = dayKey;
    this.expandedDayKey = dayKey;
    this.expandedDayEvents = [];

    try {
      const request = this.createCalendarEventsByDateRequest(date);
      const response = await this.maintenanceService.getCalendarEventsByDate(request, this.cancelSubject);

      this.expandedDayEvents = (response.data ?? []).map(event => this.mapCalendarEvent(event));
      this.expandedDayLoadingKey = null;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.expandedDayLoadingKey = null;
        }
      });
    }
  }

  private async loadAllCalendarEventsForRange(showLoader: boolean = true): Promise<void> {
    if (showLoader) {
      this.isLoadingAllEvents = true;
    }

    try {
      const { timeFrom, timeTo } = this.getCalendarDateRange();
      const request: CalendarEventsByDateRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom,
        timeTo,
        date: timeFrom,
        endOfDay: timeTo,
        maintenanceTypes: this.getSelectedMaintenanceTypes(),
        showMissedEvents: this.shouldShowMissedEvents(),
        searchTerm: this.searchTerm
      };

      const response = await this.maintenanceService.getCalendarEventsByDate(request, this.cancelSubject);

      this.previewEvents = (response.data ?? []).map(event => this.mapCalendarEvent(event));
      if (showLoader) {
        this.isLoadingAllEvents = false;
      }
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          if (showLoader) {
            this.isLoadingAllEvents = false;
          }
        }
      });
    }
  }

  private createCalendarEventsPreviewRequest(): CalendarEventsPreviewRequest {
    const { timeFrom, timeTo } = this.getCalendarDateRange();

    return {
      accountIds: this.accountFilter.accountIds,
      timeFrom,
      timeTo,
      timezoneOffset: new Date().getTimezoneOffset(),
      maintenanceTypes: this.getSelectedMaintenanceTypes(),
      showMissedEvents: this.shouldShowMissedEvents(),
      searchTerm: this.searchTerm
    };
  }

  private createCalendarEventsByDateRequest(date: Date): CalendarEventsByDateRequest {
    const { timeFrom, timeTo } = this.getCalendarDateRange();

    return {
      accountIds: this.accountFilter.accountIds,
      timeFrom,
      timeTo,
      date,
      endOfDay: endOfDay(date),
      maintenanceTypes: this.getSelectedMaintenanceTypes(),
      showMissedEvents: this.shouldShowMissedEvents(),
      searchTerm: this.searchTerm
    };
  }

  private createTodayCalendarRequest(): TodayCalendarEventsPaginationRequest {
    return {
      accountIds: this.accountFilter.accountIds,
      timeFrom: startOfDay(new Date()),
      timeTo: endOfDay(new Date()),
      page: this.todayPage,
      pageSize: 20
    };
  }

  private mapCalendarEvent(event: CalendarEventResponse): CalendarEvent {
    const scheduledDate = new Date(event.scheduledDate);

    return {
      id: event.id,
      assetId: event.assetId,
      assetName: event.assetName,
      type: this.normalizeCalendarEventType(event.type),
      startTime: this.formatDateTimeToTime(scheduledDate),
      scheduledDate,
      isMissedEvent: event.isMissedEvent
    };
  }

  private mapTodayEvent(event: TodayCalendarEventsResponse): CalendarTodayEvent {
    const startTime = new Date(event.startTime);
    const endTime = event.endTime ? new Date(event.endTime) : null;

    return {
      id: event.id,
      assetId: event.assetId,
      assetName: event.assetName,
      type: this.normalizeCalendarEventType(event.type),
      startTime: this.formatDateTimeToTime(startTime),
      endTime: endTime ? this.formatDateTimeToTime(endTime) : null,
      workshop: event.workshopName?.trim() ? event.workshopName : ''
    };
  }

  private normalizeEventScheduleResponse(eventSchedule: EventScheduleResponse): EventScheduleResponse {
    return {
      ...eventSchedule,
      odometerReading: eventSchedule.odometerReading.toLocaleString(),
      scheduledDate: new Date(eventSchedule.scheduledDate),
      serviceItems: eventSchedule.serviceItems ?? []
    };
  }

  private mergeTargetDateWithEventTime(targetDate: Date, sourceDateTime: Date): Date {
    return new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate(),
      sourceDateTime.getHours(),
      sourceDateTime.getMinutes(),
      sourceDateTime.getSeconds(),
      sourceDateTime.getMilliseconds()
    );
  }

  private applyOptimisticEventDrop(eventId: number, scheduledDate: Date): void {
    this.previewEvents = this.previewEvents.map(event => {
      if (event.id !== eventId) {
        return event;
      }

      return {
        ...event,
        scheduledDate: new Date(scheduledDate),
        startTime: this.formatDateTimeToTime(scheduledDate)
      };
    });
  }

  private getCalendarDateRange(): { timeFrom: Date; timeTo: Date } {
    return {
      timeFrom: this.dateFilter.startDate ?? startOfMonth(this.currentDate),
      timeTo: this.dateFilter.endDate ?? endOfMonth(this.currentDate)
    };
  }

  private normalizeCalendarEventType(type: string): 'preventative' | 'reactive' {
    const normalizedType = type.trim().toLowerCase();

    switch (normalizedType) {
      case 'preventative':
        return 'preventative';
      case 'reactive':
      default:
        return 'reactive';
    }
  }

  private getSelectedMaintenanceTypes(): MaintenanceEventType[] {
    return this.calendarConfig.filterOptions
      .filter(option => option.checked)
      .map(option => {
        switch (option.key) {
          case 'preventative':
            return MaintenanceEventType.Preventative;
          case 'reactive':
            return MaintenanceEventType.Reactive;
          default:
            return option.label as MaintenanceEventType;
        }
      });
  }

  private shouldShowMissedEvents(): boolean {
    return this.calendarConfig.missedEventFilter?.checked ?? false;
  }

  private clearExpandedDayEvents(): void {
    this.expandedDayKey = null;
    this.expandedDayEvents = [];
  }

  private getDayKey(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private cloneCalendarEvents(events: CalendarEvent[]): CalendarEvent[] {
    return events.map(event => ({
      ...event,
      scheduledDate: new Date(event.scheduledDate)
    }));
  }

  private formatDateTimeToTime(value: Date): string {
    const hours = `${value.getHours()}`.padStart(2, '0');
    const minutes = `${value.getMinutes()}`.padStart(2, '0');
    return `${hours}:${minutes}`;
  }
}